/* @flow */


import { apiVersion } from '../utils';
import { extend } from './coreutils';
import { parseJSON, isJSONSupported } from './browserutil';

declare type configCacheType = {
  expired: boolean,
  updatedTime: number,
  data: adConfig
};

declare type storageData = {
  __version: string,
  __updateTime: number,
  settings: adConfigSettings
};
/**
 * Local Storage Utility from EOW
 * {@link module:utils}
 *
 * @exports utils/Storage
 * @requires module:utils
 */
class _Storage {
  _isSupported: boolean;

  _storage: Storage;

  _expiry: number;

  _defaultRet: StorageDefaultResp;

  /**
   * Creates _Storage Object
   */
  constructor() {
    this._storage = window.localStorage;
    this._isSupported = this._detect() && isJSONSupported();
    this._expiry = 24 * 60 * 60 * 1000;
  }

  isSupported(): boolean {
    return this._isSupported;
  }

  /**
   * _get config name
   *
   * @access private
   * @param {string} site - Site name
   * @returns {string}
   */
  _getConfigName(site: string): string {
    return `__config_${site}`;
  }

  /**
   * store in localstorage
   *
   * @param {string} name - key to use for storage
   * @param {string} str - string to be stored
   * @returns {boolean} - status of storage, returns false is storage is not supported
   */
  store(name: string, str: string): boolean {
    if (!this._isSupported) {
      return false;
    }
    this._storage.setItem(name, str);
    return true;
  }

  /**
   * store in localstorage
   *
   * @param {object} config - string to be stored
   * @param {string} site - sitename
   * @returns {boolean} - status of storage, returns false is storage is not supported
   */
  storeConfig(config: {}, site: string): boolean {
    if (!this._isSupported) {
      return false;
    }
    const obj: storageData = extend(true, {}, config, {
      __version: apiVersion(),
      __updateTime: Date.now()
    });
    return this.store(this._getConfigName(site), JSON.stringify(obj));
  }

  /**
   * get obj from localstorage
   *
   * @param {string} name - key to use for retrieval
   * @returns {string} - data
   */
  get(name: string): string {
    if (!this._isSupported) {
      return '';
    }
    return this._storage.getItem(name) || '';
  }

  /**
   * get obj from localstorage
   *
   * @param {string} site - sitename
   * @returns {object} - expiry status and data
   */
  getConfig(site: string): configCacheType {
    if (!this._isSupported) {
      return { expired: true, data: {}, updatedTime: 0 };
    }
    const str = this.get(this._getConfigName(site)) || '{}';
    const obj: storageData = parseJSON(window, str);
    const versionMisMatch = !(
      obj &&
      '__version' in obj &&
      apiVersion() === obj.__version
    );
    const expired =
      versionMisMatch ||
      !(
        obj &&
        '__updateTime' in obj &&
        obj.__updateTime + this._expiry > Date.now()
      );
    const ret = {
      expired,
      data: versionMisMatch ? {} : obj,
      updatedTime: obj && '__updateTime' in obj ? obj.__updateTime : 0
    };
    return ret;
  }

  /**
   * Detect localStorage support in browser
   *
   * @see https://github.com/Modernizr/Modernizr/blob/master/feature-detects/storage/localstorage.js
   * @access private
   * @returns {boolean} - localstorage support status
   */
  _detect(): boolean {
    const mod = 'storagetest';
    try {
      this._storage.setItem(mod, mod);
      this._storage.removeItem(mod);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Remove in localstorage
   *
   * @param {string} name - key to use for remove
   *@returns {undefined}
   */
  remove(name: string = '') {
    const id = this.get(name);
    if (id.length) {
      this._storage.removeItem(name);
    }
  }
}

// export singleton object as default for this module
const st: _Storage = new _Storage();
export default st;
